// add this import near the top with the others
import StaffDev from './staff_dev';
import './ai-modal.css';
// add this import near the top with the others
import RouteAnalysis from "./route_analysis";
import Services from "./services"; // <-- ADDED
import SMSQueue from "./SMSQueue";
import PwaDev from "./pwa_dev";
import SOPUpload from "./SOPUpload"; // <-- ADDED: SOP Upload & Cascade

import React, { useState, useMemo, useRef, useEffect } from "react";
import Login from "./Login";
import QMap from "./QMap";
import StockFinancials from "./stock_financials";
import StockGraphs from "./stock_graphs";
import Mockup from "./Mockupp";
import StockExcess from "./stock_excess";
import WarehouseView from "./warehouse_view";
import StockInvoices from "./stock_invoices";
import ShelfPictorialView from "./shelfPictoriallView";
import Pickups from "./pickups";
import Settings from "./settings";
import Shelves from "./shelves";
import Updates from './updates';
import Stafflogin from './stafflogin';
import Sms from './sms';
import SmsProd from './smsprod';
import Sitedetails from './sitedetails';
import DriverRoute from "./driverroute";
import ApptsAnalysis from "./appts_analysis"; // <-- added
import Epos from "./epos"; // <-- added
import Nms from "./nms"; // <-- added
import HeadOffice from "./HeadOffice"; // <-- added: fleet dashboard
import SinglePharmacy from "./SinglePharmacy";
import SingleStaff from "./SingleStaff"; // <-- added: one staff member's whole file
import BusinessInsights from "./BusinessInsights";
import DashboardPanels from "./DashboardPanels";
import ClinicalRecord from "./ClinicalRecord";
import OrgSettings from "./OrgSettings"; // <-- added: single-pharmacy view
import Communications from "./Communications"; // <-- added: staff/manager push notification settings
import Navigator from "./Navigator"; // <-- added: floating "where am I / how do I" panel
import Staff from "./Staff"; // <-- added: staff roster + appraisals
import HolidayRota from "./holiday_rota"; // <-- added: per-branch holiday/absence rota
import EstateInspection from "./estate_inspection"; // <-- added: estate-wide inspection snapshot
import DeliveryActivity from "./DeliveryActivity"; // <-- added: delivery activity bar chart
import Payments from "./Payments"; // <-- added: delivery payment plans by branch
import Workforce from "./Workforce"; // <-- added: hours per 1000 items grid
import Inspection from "./Inspection"; // <-- added: branch inspection workbench
import Config from "./Config"; // <-- added: configurables admin
import RegulatoryCompliance from "./RegulatoryCompliance"; // <-- added: regulatory compliance dashboard
import CqcSites from "./CqcSites"; // <-- added: CQC compliance dashboard (sister to Regulatory Compliance)
import CqcWorklist from "./CqcWorklist"; // <-- added: CQC Worklist — flat cross-site "what's outstanding" view
import CqcInspectorView from "./CqcInspectorView"; // <-- added: Admin > CQC Inspector view (Pharmace all only)
import CqcContributorTask from "./CqcContributorTask"; // <-- added: public "?cqcTask=TOKEN" contributor portal (no login)
import SiteManagement from "./SiteManagement"; // <-- added: Admin > Site management (Pharmace all only)
import InvoiceProcessing from "./invoice_processing"; // <-- added: Admin > Invoice processing (Pharmace all only)
import PharmacistRota from "./PharmacistRota"; // <-- added: pharmacist cover rota (beta)

import QuestionBox from "./QuestionBox";
import "./App.css";

import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

const App = () => {
  // ---------------------------------------------------------------------------
  // Read-only single-pharmacy viewing link: /?spview=TOKEN (minted by the
  // "Create viewing URL" button on the Single pharmacy page). The token is the
  // credential — no login, no nav rail, one pharmacy, view-only.
  // NOTE: this early return runs before ANY hook, so hook order stays stable.
  // ---------------------------------------------------------------------------
  const spviewToken = new URLSearchParams(window.location.search).get("spview");
  if (spviewToken) {
    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <SinglePharmacy viewToken={spviewToken} />
      </LocalizationProvider>
    );
  }

  // ---------------------------------------------------------------------------
  // CQC contributor-task link: /?cqcTask=TOKEN (emailed from the "Ask a
  // contributor" panel in CqcEvidenceDialog.js). Same shape as ?spview=TOKEN
  // above — the token is the credential, no login, no nav rail, scoped to
  // exactly one evidence element. NOTE: this early return runs before ANY
  // hook, so hook order stays stable.
  // ---------------------------------------------------------------------------
  const cqcTaskToken = new URLSearchParams(window.location.search).get("cqcTask");
  if (cqcTaskToken) {
    return <CqcContributorTask token={cqcTaskToken} />;
  }

  return <AuthedApp />;
};

const AuthedApp = () => {
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [userData, setUserData] = useState(null);
  const [currentView, setCurrentView] = useState("head_office");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [shelvesOpen, setShelvesOpen] = useState(false);
  const [answer, setAnswer] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  const [loadingAnswer, setLoadingAnswer] = useState(false);
  const [clarificationOptions, setClarificationOptions] = useState(null);
  const [selectedPharmacy, setSelectedPharmacy] = useState("");
  const [selectedMetric, setSelectedMetric] = useState("");
  const [showStockMenu, setShowStockMenu] = useState(false);

  // Unified state for the categorised dropdown menus.
  // Only one dropdown is open at a time. Click-outside closes it.
  const [openMenu, setOpenMenu] = useState(null); // "operations" (Staff) | "stock" | "services" | "admin" | "settings" | null
  const navRef = useRef(null);

  // Pale-grey button style for the left nav rail (beats global blue button rule via inline style)
  const navBtnStyle = {
    backgroundColor: "#f3f4f6",
    color: "#374151",
    border: "1px solid #d1d5db",
    borderRadius: 4,
    padding: "6px 10px",
    fontSize: 14,
    textAlign: "left",
    width: "100%",
    cursor: "pointer"
  };

  // ---------------------------------------------------------------------------
  // ACTIVE RAIL HIGHLIGHTING
  // ---------------------------------------------------------------------------
  // Which flyout owns which routes, so a parent ("Stock", "Delivery") lights up
  // when you are inside one of its children. Keys match the openMenu values.
  const NAV_GROUPS = {
    delivery: ["route_analysis", "delivery_activity", "payments"],
    stock:    ["stock_financials", "shelfPictorialView", "stock_excess", "stock_graphs",
               "stock_invoices", "warehouse_view"],
    services: ["services", "nms", "epos"],
    admin:    ["qmap", "pickups", "smsprod", "sms", "staff_dev", "pwa_dev",
               "sms_queue", "sitedetails", "site_management", "invoice_processing", "cqc_inspector"]
  };

  // navBtn(view, extra) — the rail style with an active variant.
  //
  // The active look is a thick teal left edge plus a teal border, NOT a
  // background swap. Several rail buttons carry a meaningful colour of their
  // own (Regulatory Compliance is blue, Custom panels amber); replacing the
  // background to show selection would destroy that signal. The left edge sits
  // on top of any colour, so both survive. paddingLeft drops by the extra
  // border width so the label doesn't jump sideways when selected.
  const navBtn = (view, extra = {}) => {
    // `view` may be a route name, a list of route names, or a plain boolean —
    // the last for buttons that open a dialog rather than change currentView
    // (Settings, Stock Shelves), which are just as much "where you are".
    const on = typeof view === "boolean"
      ? view
      : Array.isArray(view) ? view.includes(currentView) : currentView === view;
    if (!on) return { ...navBtnStyle, ...extra };
    return {
      ...navBtnStyle,
      ...extra,
      borderColor: "#0f766e",
      borderLeft: "4px solid #0f766e",
      paddingLeft: 7,
      fontWeight: 700,
      color: extra.color || "#0f4c47",
      backgroundColor: extra.backgroundColor || extra.background || "#ccfbf1",
      boxShadow: "0 0 0 1px rgba(15,118,110,0.12)"
    };
  };

  // Same treatment for a flyout parent whose child route is the current view.
  const navGroupBtn = (groupKey, extra = {}) => navBtn(NAV_GROUPS[groupKey] || [], extra);

  // Navigator — the floating "where am I / how do I" panel at the foot of the
  // rail. Kept in App because it needs currentView and goTo, and because it must
  // outlive any single view: it stays open while you follow its instructions.
  const [navigatorOpen, setNavigatorOpen] = useState(false);

  useEffect(() => {
    function handleClickOutside(e) {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setOpenMenu(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Helper: choose a view and close any open dropdown.
  // navEpoch increments on EVERY rail click and keys the rendered view, so
  // clicking a menu button always lands fresh at that section's top level —
  // including re-clicking the section you are already in (previously a
  // silent no-op: setCurrentView with the same value doesn't re-render, so
  // deep drill-down state inside the section appeared "stuck").
  const [navParams, setNavParams] = useState(null);
  const [navEpoch, setNavEpoch] = useState(0);
  const goTo = (view, params = null) => {
    setCurrentView(view);
    setNavParams(params);
    setNavEpoch(e => e + 1);
    setOpenMenu(null);
  };

  // Pagination & Sorting & Filtering
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 6;
  const [sortKey, setSortKey] = useState("");
  const [sortAsc, setSortAsc] = useState(true);
  const [filterText, setFilterText] = useState("");
  const [question, setQuestion] = useState("");

  const handleShelvesClick = () => setShelvesOpen(true);
  const handleSettingsClick = () => setSettingsOpen(true);

  /* ---------------------------------------------------------------------------
     CLINICAL RECORD - optional module.

     The nav button is hidden unless the organisation has switched the module on
     in Settings -> Organisation Settings -> Modules. The flag is the group
     setting `clinical_record`, mirrored into localStorage by Login.js at sign-in
     and by the Settings dialog when it saves.

     It is held in STATE rather than read from localStorage at render time, so
     that saving the setting shows or hides the button straight away instead of
     waiting for the next page load. The value is re-read when the Settings
     dialog closes (see the <Settings /> onClose below).

     Absent means off: an organisation that has never seen this setting gets the
     button hidden, which is how it behaved before.
     --------------------------------------------------------------------------- */
  const CLINICAL_RECORD_LS = "clinical_record";
  const readClinicalRecordEnabled = () => {
    try {
      return JSON.parse(localStorage.getItem(CLINICAL_RECORD_LS) || "false") === true;
    } catch (_) {
      return false;
    }
  };
  const [clinicalRecordVisible, setClinicalRecordVisible] = useState(readClinicalRecordEnabled);

  // Switching the module off while the page is on screen must not leave the
  // user staring at an empty panel, so send them back to the head office view.
  useEffect(() => {
    if (!clinicalRecordVisible && currentView === "clinical_record") {
      setCurrentView("head_office");
    }
  }, [clinicalRecordVisible, currentView]);

  /* ---------------------------------------------------------------------------
     CQC COMPLIANCE + CQC WORKLIST - Pharmace all only (Aug 2026).

     The two CQC rail buttons are hidden for every other organisation. Matched
     case-insensitively so "Pharmace all" and "Pharmace All" both pass. The CQC
     Inspector view already sits inside the Admin flyout, which has its own
     Pharmace all gate below, so nothing changes there.

     Same safety net as Clinical Record: if a CQC view is somehow current for
     an organisation that cannot see the buttons, land on Head Office rather
     than a screen with no way back.
     --------------------------------------------------------------------------- */
  const isPharmaceAll =
    (localStorage.getItem("organisationName") || "").trim().toLowerCase() === "pharmace all";

  useEffect(() => {
    if (!isPharmaceAll && (currentView === "cqc_compliance" || currentView === "cqc_worklist")) {
      setCurrentView("head_office");
    }
  }, [isPharmaceAll, currentView]);

  /* ---------------------------------------------------------------------------
     "Appraisal embedded" (menu button; Pharmace all + Southtown Group only).
     Opens the standalone embeddable-appraisal page on pharmaceinstall:
       https://pharmaceinstall.co.uk/appraisals.html?key=wk_...
     The full widget key is only returned once at creation (the list endpoint
     returns tails), so the first click creates a dedicated demo key for the
     'Demo Live' pharmacy (origin-locked to pharmaceinstall) and the finished
     link is remembered in this browser (localStorage) for every later click.
     --------------------------------------------------------------------------- */
  const APPRAISAL_DEMO_LS = "appraisal_embedded_demo_url";
  const [appraisalDemoBusy, setAppraisalDemoBusy] = useState(false);
  const appraisalDemoVisible = ["pharmace all", "southtown group"]
    .includes((localStorage.getItem("organisationName") || "").trim().toLowerCase());

  const openAppraisalEmbeddedDemo = async () => {
    const saved = localStorage.getItem(APPRAISAL_DEMO_LS) || "";
    if (saved.startsWith("https://pharmaceinstall.co.uk/appraisals.html?key=wk_")) {
      window.open(saved, "_blank", "noopener");
      return;
    }
    let members = [];
    try { members = JSON.parse(localStorage.getItem("members")) || []; } catch (_) {}
    // The demo is always keyed to the 'Demo Live' pharmacy; fall back to the
    // first pharmacy only if Demo Live isn't a member of this organisation.
    const demoMember = members.find(m => String(m?.pharmacyName || "").trim().toLowerCase() === "demo live");
    const targetName = demoMember?.pharmacyName || members[0]?.pharmacyName || "";
    if (!targetName) { window.alert("No pharmacy found on this organisation to demo with."); return; }
    if (!window.confirm(
      `Open the embedded appraisal demo?\n\nThis creates a demo widget key for "${targetName}"` +
      (demoMember ? "" : " (no 'Demo Live' pharmacy was found, so the first pharmacy is used)") +
      ` — locked to pharmaceinstall.co.uk — and opens the standalone appraisal page with it. ` +
      `The link is remembered in this browser for next time.`)) return;
    setAppraisalDemoBusy(true);
    try {
      const res = await fetch(`https://api.pharmacegroup.co.uk/api/appraisals/widget-keys/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken") || localStorage.getItem("token")}`
        },
        body: JSON.stringify({
          pharmacyName: targetName,
          label: "Appraisal embedded demo (menu)",
          allowedOrigins: ["https://pharmaceinstall.co.uk"],
          issuedBy: { name: localStorage.getItem("userName") || "", roleKey: localStorage.getItem("roleKey") || "GROUP_MANAGER" }
        })
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || `HTTP ${res.status}`);
      const url = `https://pharmaceinstall.co.uk/appraisals.html?key=${d.key}`;
      try { localStorage.setItem(APPRAISAL_DEMO_LS, url); } catch (_) {}
      window.open(url, "_blank", "noopener");
    } catch (e) { window.alert(`Could not open the demo: ${e.message}`); }
    finally { setAppraisalDemoBusy(false); }
  };

  /* ---------------------------------------------------------------------------
     "Insert demo data" (menu button; Southtown Group only). Calls the backend
     demo seeder (POST /api/demoseed/run), which INSERTS artificial:true demo
     records into the four hardcoded test databases, high-water-mark style:
       - continues each staff member's clock history from their last seeded day
       - TODAY gets clock-in-only sessions (staff haven't left yet)
       - any earlier artificial sessions still missing a clock-out are closed
         retrospectively ("orphan cleanup")
     The server re-checks the organisation, so the button is cosmetic gating
     only; nothing outside the four test databases can ever be written.
     --------------------------------------------------------------------------- */
  const [demoSeedBusy, setDemoSeedBusy] = useState(false);
  const demoSeedVisible = (localStorage.getItem("organisationName") || "").trim().toLowerCase() === "southtown group";

  const runDemoSeed = async () => {
    if (!window.confirm(
      "Refresh demo data in the four Southtown test sites?\n\n" +
      "CLOCK IN/OUT HISTORY IS REPLACED. Existing demo clock sessions and break logs for " +
      "the seeding window are DELETED and regenerated, so a re-run reflects the current " +
      "generator rather than adding a second batch alongside the first.\n\n" +
      "Everything else is insert-only and left as it is: staff, roles, schedules, " +
      "appraisals, pre-appraisals and 12-month reviews. Staff records keep their ids, so " +
      "appraisal history survives.\n\n" +
      "Only records flagged artificial:true are ever deleted - real clockings in those " +
      "test sites are untouched, and days that already hold a real clocking are skipped.\n\n" +
      "Today's sessions get a clock-in only (staff are still on site).\n\nProceed?")) return;
    setDemoSeedBusy(true);
    try {
      const res = await fetch(`https://api.pharmacegroup.co.uk/api/demoseed/run`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken") || localStorage.getItem("token")}`
        },
        body: JSON.stringify({ mode: "seed" })
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || `HTTP ${res.status}`);
      const t = d.totals || {};
      window.alert(
        "Demo data refreshed across the four test sites:\n\n" +
        `Clock sessions replaced: ${t.attendanceDeleted || 0} deleted, ${t.attendance || 0} regenerated\n` +
        `  (of which ${t.todayPartials || 0} are today's clock-in-only)\n` +
        `Break logs: ${t.breaksDeleted || 0} deleted, ${t.breaks || 0} regenerated\n` +
        (t.attendanceKeptOlder
          ? `Older than the seeding window, left alone: ${t.attendanceKeptOlder}\n`
          : "") +
        `Orphan sessions closed: ${t.orphansClosed || 0}\n\n` +
        "Left as they were (insert-only):\n" +
        `Staff created: ${t.staffCreated || 0} | Schedule rows: ${t.timesDocs || 0}\n` +
        `Appraisals: ${t.appraisals || 0} | Pre-appraisals: ${t.preappraisals || 0} | 12-month reviews: ${t.reviews || 0}\n` +
        `Holiday requests: ${t.leaveRequests || 0} (${t.leaveApproved || 0} approved, ` +
        `${t.leaveRejected || 0} rejected, ${t.leavePending || 0} awaiting)\n` +
        `Absences: ${t.absences || 0} spells over ${t.absenceDays || 0} days\n` +

        "Only artificial:true records were deleted.");
    } catch (e) { window.alert(`Demo data insert failed: ${e.message}`); }
    finally { setDemoSeedBusy(false); }
  };

  /* ---------------------------------------------------------------------------
     "Wipe demo data" — the same endpoint in wipe mode.
     Exists because the CLI cannot be used on this host: the Mongo connection
     strings live inside server.js rather than in an .env the script can read,
     so `node seed_southtown_demo.js --wipe` falls back to localhost and is
     refused. In-process is the only way to reach a wipe.
     Deletes ONLY artificial:true records, and only in the four hardcoded test
     databases — the server re-checks both, so this button is cosmetic gating.
     --------------------------------------------------------------------------- */
  const [demoWipeBusy, setDemoWipeBusy] = useState(false);

  const runDemoWipe = async () => {
    if (!window.confirm(
      "Delete ALL demo data from the four Southtown test sites?\n\n" +
      "Removes artificial:true staff, schedules, clock sessions, break logs, " +
      "appraisals, pre-appraisals, reviews, holiday requests, absences and " +
      "goods-in invoices.\n\n" +
      "Real records are never touched: every delete is filtered on " +
      "artificial:true, and nothing outside the four test databases is opened.\n\n" +
      "Invoices raised by hand in the app carry no artificial flag and will " +
      "SURVIVE this.\n\nProceed?")) return;
    setDemoWipeBusy(true);
    try {
      const res = await fetch(`https://api.pharmacegroup.co.uk/api/demoseed/run`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken") || localStorage.getItem("token")}`
        },
        body: JSON.stringify({ mode: "wipe" })
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || `HTTP ${res.status}`);
      const lines = (d.report || []).map((r) => {
        const cols = Object.entries(r)
          .filter(([k, v]) => k !== "dbName" && typeof v === "number" && v > 0)
          .map(([k, v]) => `  ${k}: ${v}`);
        return `${r.dbName}\n${cols.length ? cols.join("\n") : "  nothing to delete"}`;
      });
      window.alert("Demo data wiped.\n\n" + lines.join("\n\n") +
        "\n\nOnly artificial:true records were deleted. " +
        "Use Insert demo data to regenerate.");
    } catch (e) { window.alert(`Demo data wipe failed: ${e.message}`); }
    finally { setDemoWipeBusy(false); }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    setToken(null);
  };

  const handleAskQuestion = async (question) => {
    if (!question.trim()) return;
    setQuestion(question);
    setLoadingAnswer(true);
    setClarificationOptions(null);
    setAnswer("");
    setCurrentPage(1);
    const members = JSON.parse(localStorage.getItem("members") || "[]");
    try {
      const res = await fetch("https://api.pharmacegroup.co.uk/aiquery", {
        method: "POST",
        headers: { "Content-Type": "application/json",
  "Authorization": `Bearer ${token}` },
        body: JSON.stringify({ question, members })
      });

      if (!res.ok) {
        console.error("Server returned an error status:", res.status);
        setAnswer(`Server error: ${res.status}`);
        setShowDialog(true);
        return;
      }

      const data = await res.json();
      if (data.needClarification) {
        setClarificationOptions({
          message: data.message,
          missingFields: data.missingFields
        });
        return;
      }

      setAnswer(data.answer || "No response received.");
      setShowDialog(true);

      await fetch("https://api.pharmacegroup.co.uk/savequestion", {
        method: "POST",
        headers: { "Content-Type": "application/json",
  "Authorization": `Bearer ${token}` },
        body: JSON.stringify({
          groupId: localStorage.getItem("userId"),
          question
        })
      });
    } catch (error) {
      console.error("Error during /aiquery fetch:", error);
      setAnswer("An error occurred while fetching data.");
      setShowDialog(true);
    } finally {
      setLoadingAnswer(false);
    }
  };

  const handleClarifiedQuery = async () => {
    if (!selectedPharmacy || !selectedMetric) return;
    const newQuestion = `What is the ${selectedMetric} at ${selectedPharmacy}?`;
    await handleAskQuestion(newQuestion);
  };


  const handleExportToExcel = () => {
    if (!Array.isArray(answer) || answer.length === 0) return;

    const exportData = answer.map(item => {
      const filtered = {};
      for (const [key, value] of Object.entries(item)) {
        if (["_id", "log"].includes(key)) continue;
        if (["price", "value", "totalPrice", "totalValue"].includes(key) && typeof value === "number") {
          filtered[key] = (value / 100).toFixed(2);
        } else {
          filtered[key] = value;
        }
      }
      return filtered;
    });

    import("xlsx").then((xlsx) => {
      const workbook = xlsx.utils.book_new();
      const worksheet = {};

      const groupName = localStorage.getItem("organisationName") || "Group";
      const now = new Date();
      const formatted = now.toLocaleString("en-GB", {
        day: "2-digit", month: "2-digit", year: "2-digit",
        hour: "2-digit", minute: "2-digit"
      });
      const questionPrompt = question || "";

      const headerRows = [
        [groupName],
        [`Generated at: ${formatted}`],
        [`Prompt: ${questionPrompt}`],
        []
      ];
      xlsx.utils.sheet_add_aoa(worksheet, headerRows, { origin: "A1" });

      const headers = Object.keys(exportData[0] || {});
      xlsx.utils.sheet_add_aoa(worksheet, [headers], { origin: "A5" });

      xlsx.utils.sheet_add_json(worksheet, exportData, {
        origin: "A6",
        skipHeader: true
      });

      const allRows = [...headerRows, headers, ...exportData.map(obj => headers.map(k => obj[k] ?? ""))];
      worksheet["!cols"] = headers.map((_, colIdx) => {
        const maxLength = allRows.reduce((max, row) => {
          const val = row[colIdx];
          const len = val ? String(val).length : 0;
          return Math.max(max, len);
        }, 10);
        return { wch: maxLength + 2 };
      });

      xlsx.utils.book_append_sheet(workbook, worksheet, "AI_Results");
      xlsx.writeFile(workbook, "AI_Response.xlsx");
    });
  };

  const filteredAndSorted = useMemo(() => {
    if (!Array.isArray(answer)) return [];

    let result = [...answer];

    if (filterText) {
      result = result.filter(row =>
        Object.values(row).some(val =>
          String(val).toLowerCase().includes(filterText.toLowerCase())
        )
      );
    }

    if (sortKey) {
      result.sort((a, b) => {
        const valA = a[sortKey];
        const valB = b[sortKey];
        if (valA == null) return 1;
        if (valB == null) return -1;
        if (typeof valA === "number" && typeof valB === "number") {
          return sortAsc ? valA - valB : valB - valA;
        }
        return sortAsc
          ? String(valA).localeCompare(String(valB))
          : String(valB).localeCompare(String(valA));
      });
    }

    return result;
  }, [answer, filterText, sortKey, sortAsc]);

  const totalPages = Math.ceil(filteredAndSorted.length / pageSize);
  const paginatedAnswer = filteredAndSorted.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  // ---- wall display -------------------------------------------------------
  // ?wall=<dashboardId> renders one dashboard and nothing else: no menu, no
  // header, no chrome. This is for a screen on the wall, so anything that is
  // not the panels is wasted pixels - and there is nothing to click by
  // accident. The token still comes from localStorage, which is shared across
  // tabs on the same origin, so the new tab is already signed in.
  const wallDash = new URLSearchParams(window.location.search).get("wall");
  if (wallDash && token) {
    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DashboardPanels wallDashboardId={wallDash} />
      </LocalizationProvider>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <div className="app-container">
        {!token ? (
          <Login setToken={setToken} setUserData={setUserData} />
        ) : (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: "15px", marginTop: "4px", marginBottom: "4px", flexWrap: "wrap" }}>
              <h2 className="group-title" style={{ margin: 0, whiteSpace: "nowrap" }}>
                {localStorage.getItem("organisationName") || "Group"} management tools
              </h2>
              {/* Section-header slot: section components (Head Office etc.)
                  portal their own heading + toolbars in here so the page
                  title sits ON this line instead of burning a row below. */}
              <div id="section-header-slot"
                   style={{ display: "flex", alignItems: "center", gap: 16, flex: 1, minWidth: 0, flexWrap: "wrap" }} />
              {/* Hidden per request (kept in code — restore by removing this
                  comment wrapper): the ask-me-a-question bar, its answer
                  button and the Suggestions toggle all live inside
                  QuestionBox.
              <QuestionBox onAsk={handleAskQuestion} />
              */}
            </div>

            {/* ── Layout split: left navigation rail + right content area ── */}
            <style>{`
              .nav-rail button {
                background-color: #f3f4f6;
                color: #374151;
                border: 1px solid #d1d5db;
                border-radius: 4px;
                padding: 6px 10px;
                font-size: 14px;
                cursor: pointer;
              }
              .nav-rail button:hover {
                background-color: #e5e7eb;
              }
            `}</style>
            <div style={{ display: "flex", alignItems: "flex-start", gap: "15px" }}>
              {/* Left nav rail */}
              <div
                ref={navRef}
                className="nav-rail"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "6px",
                  flexShrink: 0,
                  width: 180,
                  padding: "10px",
                  background: "#f8fafc",
                  border: "1px solid #e5e7eb",
                  borderRadius: 6,
                  position: "sticky",
                  top: 10,
                  alignSelf: "flex-start"
                }}
              >
                {/* Head Office — fleet dashboard for /api/headoffice/* — default landing page */}
                <button onClick={() => goTo("head_office")} style={navBtn("head_office")}
                  title="A live 'how am I driving?' dashboard — current, real-time status across the whole fleet at a glance.">Head Office</button>

                {/* Site Activity — most-used, kept standalone */}
                <button onClick={() => goTo("mockup")} style={navBtn("mockup")}
                  title="View aggregated activity across all sites between two chosen dates — pick a date range and see the totals.">Site Activity</button>

                {/* Single pharmacy — Head-Office-style view scoped to one pharmacy */}
                <button onClick={() => goTo("single_pharmacy")} style={navBtn("single_pharmacy")}
                  title="The Head Office dashboard scoped to one branch — shows the live status of a single pharmacy at a time.">Single pharmacy</button>

                {/* Single staff — the same idea scoped to one person */}
                <button onClick={() => goTo("single_staff")} style={navBtn("single_staff")}
                  title="Everything held about one staff member — appraisals, documents, SOP sign-off and leave requests — in one place. Includes leavers.">Single staff</button>

                {/* Inspection menu button removed per request (2026-07). The
                    "inspection" route and the Inspection component remain
                    intact — restore this block to bring the menu entry back.
                <button onClick={() => goTo("inspection")} style={{ ...navBtnStyle, backgroundColor: "#fef3c7" }}
                  title="Inspection-readiness workbench — work through a branch's evidence and readiness ahead of a GPhC inspection.">Inspection</button>
                */}

                {/* Regulatory Compliance — fleet compliance against GPhC + statutory duties */}
                <button onClick={() => goTo("regulatory_compliance")} style={navBtn("regulatory_compliance", { backgroundColor: "#dbeafe" })}
                  title="Fleet compliance against GPhC standards and statutory duties — tabbed by area, drill down to per-branch and per-item completion.">Regulatory Compliance</button>

                {/* CQC Compliance + CQC Worklist - Pharmace all only (see the
                    isPharmaceAll block above). Other organisations never see
                    either button. */}
                {isPharmaceAll && (
                  <>
                    {/* CQC Compliance — sister dashboard, CQC's Single Assessment Framework */}
                    <button onClick={() => goTo("cqc_compliance")} style={navBtn("cqc_compliance", { backgroundColor: "#ede9fe" })}
                      title="CQC compliance against the Single Assessment Framework — key questions, quality statements, drill down to per-site evidence.">CQC Compliance</button>

                    {/* CQC Worklist — flat, full-screen "what's outstanding" view across every site */}
                    <button onClick={() => goTo("cqc_worklist")} style={navBtn("cqc_worklist", { backgroundColor: "#ede9fe" })}
                      title="Every CQC evidence item across every site in one flat list — status, source, assignee, comments and messages, without drilling down.">CQC Worklist</button>
                  </>
                )}

                {/* Delivery — delivery rounds analysis + activity */}
                <div>
                  <button onClick={() => setOpenMenu(openMenu === "delivery" ? null : "delivery")} style={navGroupBtn("delivery")}
                    title="Delivery tools — driver route analysis and delivery activity over time.">
                    Delivery {openMenu === "delivery" ? "▼" : "▶"}
                  </button>
                  {openMenu === "delivery" && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 4, marginLeft: 12, marginTop: 4 }}>
                      <button onClick={() => goTo("route_analysis")} style={navBtn("route_analysis")}
                        title="Route analysis — delivery driver routes and efficiency.">Route analysis</button>
                      <button onClick={() => goTo("delivery_activity")} style={navBtn("delivery_activity")}
                        title="Activity — deliveries, mileage, and time over a date range, by site and driver.">Activity</button>
                      <button onClick={() => goTo("payments")} style={navBtn("payments")}
                        title="Payments — delivery payment plans by branch: who is covered, whose plan has expired, and who is still being delivered to on a lapsed plan.">Payments</button>
                    </div>
                  )}
                </div>

                {/* Staff flyout removed per request — Workforce planner,
                    Staff clocking in/out (Raw data) and Pharmacist rota now
                    live as tabs inside Head Office -> Staff -> Clock in/out.
                    The components and their currentView routes below remain
                    intact; restore this block to bring the menu back.
                {openMenu === "operations" && null}
                */}

                {/* Stock */}
                <div>
                  <button onClick={() => setOpenMenu(openMenu === "stock" ? null : "stock")} style={navGroupBtn("stock")}
                    title="Stock tools — financial value, a pictorial shelf view, excess stock, and shelf locations.">
                    Stock {openMenu === "stock" ? "▼" : "▶"}
                  </button>
                  {openMenu === "stock" && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 4, marginLeft: 12, marginTop: 4 }}>
                      <button onClick={() => goTo("stock_financials")} style={navBtn("stock_financials")}
                        title="Scanned items — the value of stock counted onto the shelf, by branch.">Scanned items</button>
                      {/* Lands on the overview, not the detail. The estate
                          picture is the question you arrive with; the line-by-line
                          table is where you go once you know which branch you are
                          chasing. Both depths keep this entry lit. */}
                      <button onClick={() => goTo("warehouse_view")} style={navBtn(["warehouse_view", "stock_excess"])}
                        title="Analysis and transfer — the estate stock picture, with the line-by-line detail, transfer engine, usage uploads and exports a click away.">Analysis and transfer</button>
                      <button onClick={() => goTo("stock_invoices")} style={navBtn("stock_invoices")}
                        title="Goods-in invoices — what each delivery booked in, as recorded at the time.">Invoices</button>
                      <button onClick={() => { handleShelvesClick(); setOpenMenu(null); }} style={navBtn(shelvesOpen)}
                        title="Stock shelves — shelf/location layout for stock.">Stock Shelves</button>
                    </div>
                  )}
                </div>

                {/* Services */}
                <div>
                  <button onClick={() => setOpenMenu(openMenu === "services" ? null : "services")} style={navGroupBtn("services")}
                    title="Clinical & operational services — appointments analysis, NMS, and EPOS.">
                    Services {openMenu === "services" ? "▼" : "▶"}
                  </button>
                  {openMenu === "services" && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 4, marginLeft: 12, marginTop: 4 }}>
                      <button onClick={() => goTo("services")} style={navBtn("services")}
                        title="Services and appointments analysis — uptake and activity for commissioned services.">Services / Appts analysis</button>
                      <button onClick={() => goTo("nms")} style={navBtn("nms")}
                        title="New Medicine Service — NMS activity and completion across branches.">NMS</button>
                      <button onClick={() => goTo("epos")} style={navBtn("epos")}
                        title="EPOS — till and shop transaction analysis.">EPOS</button>
                    </div>
                  )}
                </div>

                {/* Admin — gated by Pharmace all */}
                {(localStorage.getItem("organisationName") || "").trim() === "Pharmace all" && (
                  <div>
                    <button onClick={() => setOpenMenu(openMenu === "admin" ? null : "admin")} style={navGroupBtn("admin")}
                      title="Developer/admin tools (Pharmace all only) — internal utilities.">
                      Admin {openMenu === "admin" ? "▼" : "▶"}
                    </button>
                    {openMenu === "admin" && (
                      <div style={{ display: "flex", flexDirection: "column", gap: 4, marginLeft: 12, marginTop: 4 }}>
                        <button onClick={() => goTo("qmap")} style={navBtn("qmap")}
                          title="A map view of all branches and related locations.">Map</button>
                        <button onClick={() => goTo("pickups")} style={navBtn("pickups")}
                          title="Collection times — when prescriptions are being collected across branches.">Collection times</button>
                        <button onClick={() => goTo("smsprod")} style={navBtn("smsprod")}
                          title="SMS history — messages sent to patients.">SMS history</button>
                        <button onClick={() => goTo("sms")} style={navBtn("sms")}
                          title="SMS dev — internal SMS testing tools.">SMS dev</button>
                        <button onClick={() => goTo("staff_dev")} style={navBtn("staff_dev")}
                          title="Staff dev — internal staff-data tools.">STAFF dev</button>
                        <button onClick={() => goTo("pwa_dev")} style={navBtn("pwa_dev")}
                          title="PWA dev — internal patient-app tools.">PWA dev</button>
                        <button onClick={() => goTo("sms_queue")} style={navBtn("sms_queue")}
                          title="SMS queue — pending/outbound SMS queue.">SMS queue</button>
                        <button onClick={() => goTo("sitedetails")} style={navBtn("sitedetails")}
                          title="Site details — per-site configuration and metadata.">Site details</button>
                        {/* Invoice processing — the extraction pipeline and its
                            rules. Developer-facing: it reads every organisation's
                            invoices, so it lives here rather than under Stock. */}
                        <button onClick={() => goTo("invoice_processing")} style={navBtn("invoice_processing")}
                          title="Invoice processing — monitor supplier invoice extraction stage by stage, and edit the rules that drive it.">Invoice processing</button>
                        <button onClick={() => goTo("site_management")} style={navBtn("site_management")}
                          title="Site management — every organisation on the platform: members, login details, and access emails.">Site management</button>
                        {/* CQC Inspector view — every organisation's connected CQC
                            sites and compliance progress, read-only, centrally. */}
                        <button onClick={() => goTo("cqc_inspector")} style={navBtn("cqc_inspector")}
                          title="CQC Inspector view — every organisation's connected CQC sites and compliance progress, in one place.">CQC Inspector view</button>
                      </div>
                    )}
                  </div>
                )}

                {/* Dashboard panels — AI-assisted, user-composed dashboards */}
                <button onClick={() => goTo("dashpanels")} style={navBtn("dashpanels", { background: "#fbbf24", backgroundColor: "#fbbf24", borderColor: "#f59e0b", color: "#78350f", fontWeight: 700 })}
                  title="Custom live monitoring panels — built from plain-English descriptions — appointments and near miss data, refreshed on your chosen interval, arrangeable and saveable as named dashboards.">
                  Custom Live monitoring panels
                </button>

                {/* Spacer pushes Settings/Log out to the bottom */}
                <div style={{ flexGrow: 1, minHeight: 20 }}></div>

                {/* Clinical Record - reinstated, but only for organisations that
                    have switched the module on in
                    Settings -> Organisation Settings -> Modules.
                    The `clinical_record` route below is gated on the same flag. */}
                {clinicalRecordVisible && (
                  <button onClick={() => goTo("clinical_record")} style={navBtn("clinical_record")}
                    title="Clinical record - consultations (free text or template-driven), QEAX components, patient records and template library. Sandbox.">
                    Clinical Record</button>
                )}

                {/* Estate inspection — org-wide outlier snapshot. Sits with
                    Business Insights: both are whole-organisation analysis
                    rather than a module view. */}
                <button onClick={() => goTo("estate_inspection")} style={navBtn("estate_inspection")}
                  title="Estate inspection — every branch measured against its peers across activity, deliveries, staffing, attendance, safety, SOPs, services and messaging, with a written summary of the outliers.">
                  Estate Inspection</button>

                <button onClick={() => goTo("business_insights")} style={navBtn("business_insights")}
                  title="Business insights — monthly income/cost/contribution analysis per branch, with fleet comparisons, trends and recommendations.">
                  Business Insights</button>

                  {/* Settings — now a single top-level button opening the
                      three-tabbed dialog directly (Organisation Settings /
                      Site Settings / Configurables). The old collapsible
                      "Settings" folder is gone: it shared a name with the
                      button inside it, and the Organisation page it held is
                      now the Configurables tab of this same dialog. The
                      `org_settings` route below is left intact. */}
                  <button onClick={() => { handleSettingsClick(); setOpenMenu(null); }} style={navBtn(settingsOpen)}
                    title="Settings — organisation settings, site settings, and the financial configurables used by Business Insights.">
                    Settings</button>

                  {/* SOP Upload and Configurables were orphaned by the folder's
                      removal, so they sit at top level — Pharmace all only. */}
                  {(localStorage.getItem("organisationName") || "").trim() === "Pharmace all" && (
                    <>
                      {/* SOP Upload — DISABLED (button retained for later re-enable;
                          the sop_upload view render below is left intact). */}
                      <button disabled style={{ ...navBtnStyle, opacity: 0.45, cursor: "not-allowed" }}
                        title="SOP upload is currently disabled.">SOP Upload</button>
                      <button onClick={() => goTo("config")} style={navBtn("config")}
                        title="Configurables — admin-editable values used across the platform.">Configurables</button>
                    </>
                  )}

                {/* Communications — the hub for every message the platform
                    sends. Now owns the Action centre (moved out of Head Office).
                    Settings still has a Communications TAB, which is the staff
                    notification settings slice of this same page. */}
                <button onClick={() => goTo("communications")}
                  style={navBtn("communications")}
                  title="Communications — Action centre, staff notifications, rota circulations, appraisal emails, scheduled reports and patient messaging, gathered in one place.">
                  Communications</button>

                {/* Appraisal embedded demo — Pharmace all + Southtown Group only.
                    Right-click offers a reset of the remembered demo link. */}
                {appraisalDemoVisible && (
                  <button onClick={openAppraisalEmbeddedDemo} disabled={appraisalDemoBusy}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      if (!localStorage.getItem(APPRAISAL_DEMO_LS)) { window.alert("No demo link is stored in this browser."); return; }
                      if (window.confirm("Forget the stored demo link?\n\nThe next click will create a fresh demo key. The old key stays valid until revoked in Settings \u2192 Site Settings (label: 'Appraisal embedded demo').")) {
                        localStorage.removeItem(APPRAISAL_DEMO_LS);
                      }
                    }}
                    style={{ ...navBtnStyle, opacity: appraisalDemoBusy ? 0.6 : 1 }}
                    title="Opens a live example of the embeddable staff appraisal package \u2014 the standalone webpage a branch site would host, keyed to the Demo Live pharmacy. Right-click to reset the stored demo link.">
                    {appraisalDemoBusy ? "Appraisal embedded\u2026" : "Appraisal embedded"}
                  </button>
                )}

                {/* Demo data seeder - Southtown Group only, with confirmation */}
                {demoSeedVisible && (
                  <button onClick={runDemoSeed} disabled={demoSeedBusy}
                    style={{ ...navBtnStyle, opacity: demoSeedBusy ? 0.6 : 1 }}
                    title="Insert artificial demo data (staff, schedules, clock sessions, appraisals) into the four Southtown test sites. Insert-only; every record is flagged artificial:true. Today's sessions get a clock-in only; earlier open demo sessions are closed.">
                    {demoSeedBusy ? "Inserting demo data\u2026" : "Insert demo data"}
                  </button>
                )}

                {/* Wipe — same endpoint, wipe mode. Only artificial:true rows,
                    only the four test databases. */}
                {demoSeedVisible && (
                  <button onClick={runDemoWipe} disabled={demoWipeBusy || demoSeedBusy}
                    style={{ ...navBtnStyle, opacity: (demoWipeBusy || demoSeedBusy) ? 0.6 : 1 }}
                    title="Delete all artificial:true demo data from the four Southtown test sites. Real records and anything outside those four databases are never touched. Run Insert demo data afterwards to regenerate.">
                    {demoWipeBusy ? "Wiping demo data\u2026" : "Wipe demo data"}
                  </button>
                )}

                <button onClick={handleLogout} style={navBtnStyle}
                  title="Log out of the management platform.">Log out</button>

                {/* ── Navigator ─────────────────────────────────────────────
                    Below the menu block, separated by a rule so it reads as a
                    tool rather than another destination — it changes nothing
                    about where you are. Toggles the floating panel, and says
                    which it will do, so a panel dragged behind something or
                    off to one side is still findable from here. */}
                <div style={{ borderTop: "1px solid #e5e7eb", marginTop: 8, paddingTop: 8 }}>
                  <button
                    onClick={() => setNavigatorOpen(o => !o)}
                    style={{
                      ...navBtnStyle,
                      backgroundColor: navigatorOpen ? "#ccfbf1" : "#f3f4f6",
                      borderColor: navigatorOpen ? "#0f766e" : "#d1d5db",
                      color: navigatorOpen ? "#0f4c47" : "#374151",
                      fontWeight: navigatorOpen ? 700 : 400
                    }}
                    title="Opens a small floating panel that explains where you are and answers 'how do I…' questions. You can drag it out of the way and it stays open while you follow the steps.">
                    {navigatorOpen ? "Hide navigator" : "Show navigator"}
                  </button>
                </div>
              </div>

              {/* Right content area */}
              <div style={{ flex: 1, minWidth: 0 }}>

            {clarificationOptions && (
              <div style={{ marginTop: 20 }}>
                <p>{clarificationOptions.message}</p>
                {clarificationOptions.missingFields.includes("pharmacy name") && (
                  <>
                    <label>Choose Pharmacy: </label>
                    <select value={selectedPharmacy} onChange={(e) => setSelectedPharmacy(e.target.value)}>
                      <option value="">--Select--</option>
                      <option value="Oxford Pharmacy">Oxford Pharmacy</option>
                      <option value="High Street Pharmacy">High Street Pharmacy</option>
                      <option value="City Centre Pharmacy">City Centre Pharmacy</option>
                    </select>
                  </>
                )}
                {clarificationOptions.missingFields.includes("metric") && (
                  <>
                    <label> Choose Metric: </label>
                    <select value={selectedMetric} onChange={(e) => setSelectedMetric(e.target.value)}>
                      <option value="">--Select--</option>
                      <option value="totalCost">Total Cost</option>
                      <option value="count">Count</option>
                      <option value="totalPackets">Total Packets</option>
                      <option value="averagePrice">Average Price</option>
                      <option value="expiringSoon">Expiring Soon</option>
                    </select>
                  </>
                )}
                <button
                  onClick={handleClarifiedQuery}
                  disabled={
                    (clarificationOptions.missingFields.includes("pharmacy name") && !selectedPharmacy) ||
                    (clarificationOptions.missingFields.includes("metric") && !selectedMetric)
                  }
                  style={{ marginLeft: "10px" }}
                >
                  Submit Clarified Query
                </button>
              </div>
            )}

            {/* Keyed on view+epoch: every rail click remounts the target view
                fresh at its top level (see goTo). display:"contents" keeps the
                wrapper out of the flex layout. */}
            <div key={`${currentView}-${navEpoch}`} style={{ display: "contents" }}>
            {currentView === "mockup" && <Mockup userData={userData} />}
            {currentView === "head_office" && <HeadOffice token={token} />}
            {currentView === "single_pharmacy" && <SinglePharmacy token={token} goTo={goTo} />}
            {currentView === "single_staff" && <SingleStaff token={token} userData={userData} />}
            {currentView === "staff" && <Staff token={token} userData={userData} />}
            {currentView === "holiday_rota" && <HolidayRota token={token} userData={userData} />}
            {currentView === "workforce" && <Workforce token={token} userData={userData} />}
            {currentView === "pharmacist_rota" && <PharmacistRota />}
            {currentView === "inspection" && <Inspection token={token} userData={userData} />}
            {currentView === "regulatory_compliance" && <RegulatoryCompliance token={token} userData={userData} />}
            {/* Both CQC views are Pharmace all only - the rail buttons are
                gated on the same flag, and the redirect effect sends anyone
                else back to Head Office. */}
            {currentView === "cqc_compliance" && isPharmaceAll && <CqcSites token={token} userData={userData} />}
            {currentView === "cqc_worklist" && isPharmaceAll && <CqcWorklist token={token} userData={userData} />}
            {currentView === "cqc_inspector" && (
              <CqcInspectorView
                isPlatformAdmin={(localStorage.getItem("organisationName") || "").trim() === "Pharmace all"}
              />
            )}
            {currentView === "config" && <Config token={token} />}
            {currentView === "sop_upload" && <SOPUpload />}
            {currentView === "qmap" && <QMap userData={userData} />}
            {currentView === "stock_financials" && (
              <StockFinancials userData={userData} onOpenPictorial={() => goTo("shelfPictorialView")} />
            )}
            {/* No menu entry of its own - reached from the Scanned items screen
                and returned from by the button in its header. */}
            {currentView === "shelfPictorialView" && (
              <ShelfPictorialView userData={userData} onOpenFinancials={() => goTo("stock_financials")} />
            )}
            {currentView === "stock_graphs" && <StockGraphs userData={userData} />}
            {/* No menu entry of its own - "Analysis and transfer" lands on the
                overview below, and this is reached from its Detailed button. */}
            {currentView === "stock_excess" && (
              <StockExcess userData={userData} onOpenOverview={() => goTo("warehouse_view")} />
            )}
            {currentView === "warehouse_view" && (
              <WarehouseView userData={userData} onOpenDetail={() => goTo("stock_excess")} />
            )}
            {currentView === "stock_invoices" && <StockInvoices userData={userData} />}
            {currentView === "pickups" && <Pickups userData={userData} />}
            {currentView === "stafflogin" && <Stafflogin userData={userData} />}
            {currentView === "smsprod" && <SmsProd userData={userData} />}
            {currentView === "sms" && <Sms userData={userData} />}
            {currentView === "sms_queue" && <SMSQueue userData={userData} />}
            {currentView === "staff_dev" && <StaffDev userData={userData} />}
            {currentView === "pwa_dev" && <PwaDev userData={userData} />}
            {currentView === "route_analysis" && <RouteAnalysis userData={userData} initialPharmacyName={navParams?.pharmacyName} />}
            {currentView === "delivery_activity" && <DeliveryActivity userData={userData} token={token} />}
            {currentView === "payments" && <Payments userData={userData} token={token} />}

            {currentView === "services" && <Services userData={userData} />} {/* <-- ADDED */}
      {currentView === "nms" && <Nms userData={userData} />} {/* <-- ADDED */}
            {currentView === "appts_analysis" && <ApptsAnalysis userData={userData} />}
            {currentView === "epos" && <Epos userData={userData} />}
            {currentView === "sitedetails" && <Sitedetails userData={userData} />}
            {currentView === "updates" && <Updates userData={userData} />}
            {currentView === "business_insights" && <BusinessInsights />}
            {currentView === "estate_inspection" && <EstateInspection />}
            {currentView === "dashpanels" && <DashboardPanels />}
            {currentView === "clinical_record" && clinicalRecordVisible && <ClinicalRecord />}
            {currentView === "org_settings" && <OrgSettings />}
            {currentView === "communications" && <Communications goTo={goTo} />}
            {currentView === "site_management" && <SiteManagement />}
            {currentView === "invoice_processing" && <InvoiceProcessing />}
            </div>
              </div>
            </div>

            <Shelves open={shelvesOpen} onClose={() => setShelvesOpen(false)} />
            {/* Closing Settings re-reads the optional-module flags, so a toggle
                saved in the dialog takes effect on the rail immediately. */}
            <Settings
              open={settingsOpen}
              onClose={() => {
                setSettingsOpen(false);
                setClinicalRecordVisible(readClinicalRecordEnabled());
              }}
            />

            {/* Rendered here, outside the layout flex and after the dialogs, so
                its fixed positioning is relative to the viewport and its stacking
                context is the document root — "always on top" only holds if it is
                not nested inside something with its own transform or z-index.
                Unmounted rather than hidden when closed: reopening should start
                from where you are now, not from a stale question. */}
            {navigatorOpen && (
              <Navigator
                currentView={currentView}
                goTo={goTo}
                onClose={() => setNavigatorOpen(false)}
              />
            )}

            {showDialog && (
              <div style={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100vw",
                height: "100vh",
                backgroundColor: "rgba(0,0,0,0.5)",
                display: "flex",
                justifyContent: "center",
                alignItems: "flex-start",
                zIndex: 1000
              }}>
                <div style={{
                  background: "white",
                  padding: "20px",
                  borderRadius: "8px",
                  maxWidth: "90vw",
                  maxHeight: "90vh",
                  overflowY: "auto",
                  marginTop: "30px"
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                    <h3 style={{ margin: 0 }}>AI Response - </h3>
                    <div>
                      <button onClick={handleExportToExcel} style={{ marginRight: "10px" }}>Export to Excel</button>
                      <button onClick={() => setShowDialog(false)}>Close</button>
                    </div>
                  </div>

                  {Array.isArray(answer) && (
                    <>
                      <input
                        type="text"
                        placeholder="Search..."
                        value={filterText}
                        onChange={(e) => { setFilterText(e.target.value); setCurrentPage(1); }}
                        style={{ marginBottom: "10px", padding: "5px", width: "100%" }}
                      />
                      <div style={{ overflowX: "auto" }}>
                        <table style={{ borderCollapse: "collapse", width: "100%" }}>
                          <thead>
                            <tr>
                              {Object.keys(answer[0] || {}).filter(k => !["_id", "id", "log"].includes(k)).map((key) => (
                                <th key={key}
                                  onClick={() => {
                                    if (sortKey === key) {
                                      setSortAsc(!sortAsc);
                                    } else {
                                      setSortKey(key);
                                      setSortAsc(true);
                                    }
                                  }}
                                  style={{
                                    border: "1px solid #ddd",
                                    padding: "8px",
                                    backgroundColor: "#f2f2f2",
                                    textTransform: "capitalize",
                                    cursor: "pointer"
                                  }}>
                                  {key} {sortKey === key ? (sortAsc ? "↑" : "↓") : ""}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {paginatedAnswer.map((item, index) => (
                              <tr key={index}>
                                {Object.entries(item).filter(([k]) => !["_id", "id", "log"].includes(k)).map(([key, value]) => {
                                  let displayValue = value;
                                  if (["price", "value", "totalPrice", "totalValue"].includes(key) && typeof value === "number") {
                                    displayValue = `£${(value / 100).toFixed(2)}`;
                                  } else if (typeof value === "object") {
                                    displayValue = JSON.stringify(value);
                                  } else {
                                    displayValue = String(value);
                                  }

                                  const isTruncatable = ["vmppNm", "amppNm"].includes(key) && displayValue.length > 25;
                                  const cellText = isTruncatable ? displayValue.slice(0, 25) + "…" : displayValue;

                                  return (
                                    <td
                                      key={key}
                                      style={{
                                        border: "1px solid #ddd",
                                        padding: "8px",
                                        maxWidth: "200px",
                                        overflowWrap: "break-word"
                                      }}
                                      title={isTruncatable ? displayValue : ""}
                                    >
                                      {cellText}
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div style={{ marginTop: "10px", textAlign: "center" }}>
                        <button onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))} disabled={currentPage === 1}>Prev</button>
                        <span style={{ margin: "0 10px" }}>Page {currentPage} of {totalPages}</span>
                        <button onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages}>Next</button>
                      </div>
                    </>
                  )}

                  {!Array.isArray(answer) && (
                    <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                      {loadingAnswer ? "Searching..." : answer}
                    </pre>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </LocalizationProvider>
  );
};

export default App;